/**
 * Per-request stash of the full Builder page tree for SSR only.
 *
 * The catch-all loader puts the cleaned page here (keyed by the request
 * CSP nonce) so `BuilderPageView` can SSR `<Content>` with the full tree
 * without putting that tree in the React Router flight payload.
 *
 * Lifecycle contract: the loader stashes for DOCUMENT requests only
 * (`.data` requests never reach `handleRequest`, which owns the clear), and
 * `entry.server` clears the entry when the shell render settles. Concurrent
 * Workers requests are isolated by nonce (not by path — targeting can
 * produce different trees for `/`).
 *
 * This module ships in both bundles; on the client the map is simply never
 * populated, so `peekSsrBuilderPage` returns null there.
 *
 * MAX_STASH_ENTRIES is defense-in-depth against any path that stashes but
 * never clears (e.g. a middleware crash that regenerates the nonce in
 * entry.server): trees are large (potentially MBs), so an unbounded map is
 * an isolate-OOM vector. Entries only need to live from loader to shell
 * render (milliseconds), so FIFO-evicting the oldest under pressure degrades
 * a storm's SSR body (the island renders empty and the client re-fetches)
 * instead of growing memory without bound.
 */
const MAX_STASH_ENTRIES = 8;

const stash = new Map<string, unknown>();

export function stashSsrBuilderPage(key: string, page: unknown): void {
  if (!stash.has(key) && stash.size >= MAX_STASH_ENTRIES) {
    const oldest = stash.keys().next().value;
    if (oldest !== undefined) stash.delete(oldest);
  }
  stash.set(key, page);
}

export function peekSsrBuilderPage(key: string | undefined): unknown {
  if (!key) return null;
  return stash.get(key) ?? null;
}

export function clearSsrBuilderPage(key: string | undefined): void {
  if (!key) return;
  stash.delete(key);
}
